// src/pages/Explore.js
import React, { useEffect, useRef, useState } from "react";
import pb from "../lib/pb";
import { useNavigate } from "react-router-dom";
import ChallengeDetailsModal from "../components/ChallengeDetailsModal";

export default function Explore() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [openFor, setOpenFor] = useState(null);
  const [counters, setCounters] = useState({});
  const loadingRef = useRef(false);
  const navigate = useNavigate();

  const PER_PAGE = 20;

  // 🔢 aggregate analytics (views / accepts / votes)
  const aggregateCounters = async () => {
  const [views, votes] = await Promise.all([
    pb.collection("challenge_views").getFullList().catch(() => []),
    pb.collection("challenge_votes").getFullList().catch(() => []),
  ]);

  const map = {};

  for (const v of views) {
    map[v.challengeId] ??= { views:0, aye:0, no:0 };
    map[v.challengeId].views++;
  }

  for (const r of votes) {
    map[r.challengeId] ??= { views:0, aye:0, no:0 };
    if (r.vote === "aye") map[r.challengeId].aye++;
    if (r.vote === "no") map[r.challengeId].no++;
  }

  setCounters(map);
  return map;
};


  // 🔥 load challenges (infinite + trending)
  const load = async (p = 1, append = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      const res = await pb
        .collection("buzzing_challenges")
        .getList(p, PER_PAGE, {
          expand: "author",
          sort: "-created",
        });

      const counterMap = await aggregateCounters();

      const sorted = [...res.items].sort((a, b) => {
  const A = counterMap[a.id] || {};
  const B = counterMap[b.id] || {};

  return (B.aye || 0) - (A.aye || 0)        // 🔥 Impact Score
    || (B.views || 0) - (A.views || 0)
    || (B.no || 0) - (A.no || 0);
});


      setItems((prev) =>
        append ? [...prev, ...sorted] : sorted
      );

      setHasMore(res.items.length === PER_PAGE);
    } catch (err) {
      console.error("Explore load error", err);
    } finally {
      loadingRef.current = false;
    }
  };

  // initial load + realtime
  useEffect(() => {
    load(1);

    const unsubChallenges = pb
      .collection("buzzing_challenges")
      .subscribe("*", () => load(1));

    const unsubAnalytics = [
      "challenge_views",
      "challenge_accepts",
      "challenge_votes",
    ].map((c) =>
      pb.collection(c).subscribe("*", () => load(1))
    );

    return () => {
      try {
        unsubChallenges();
        unsubAnalytics.forEach((u) => u());
      } catch {}
    };
  }, []);

  // infinite scroll observer
  useEffect(() => {
    const onScroll = () => {
      if (
        window.innerHeight + window.scrollY >=
          document.body.offsetHeight - 400 &&
        hasMore
      ) {
        const next = page + 1;
        setPage(next);
        load(next, true);
      }
    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [page, hasMore]);

  return (
    <div className="page explore-page" style={{ padding: 12 }}>
      {/* header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <button onClick={() => navigate(-1)}>← Back</button>
        <h2 style={{ margin: 0 }}>Explore Challenges</h2>
      </div>

      {/* 🔥 Masonry Grid */}
      <div
        style={{
          columnCount: 3,
          columnGap: 12,
        }}
      >
        {items.map((c) => {
          const author = c.expand?.author;
          const analytics = counters[c.id] || {};

          let mediaUrl = null;
          if (Array.isArray(c.media) && c.media.length)
            mediaUrl = pb.files.getURL(c, c.media[0]);
          else if (typeof c.media === "string")
            mediaUrl = pb.files.getURL(c, c.media);

          return (
            <div
              key={c.id}
              style={{
                breakInside: "avoid",
                marginBottom: 12,
                background: "#0c0c0d",
                borderRadius: 12,
                cursor: "pointer",
                overflow: "hidden",
              }}
              onClick={() => setOpenFor(c.id)}
            >
              {/* media */}
              <div style={{ background: "#000" }}>
                {c.mediaType === "Video" && mediaUrl ? (
                  <video
                    src={mediaUrl}
                    muted
                    playsInline
                    preload="metadata"
                    style={{ width: "100%" }}
                  />
                ) : c.mediaType === "Image" && mediaUrl ? (
                  <img
                    src={mediaUrl}
                    alt=""
                    style={{ width: "100%", display: "block" }}
                  />
                ) : (
                  <div
                    style={{
                      height: 180,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#777",
                      fontSize: 13,
                    }}
                  >
                    No media
                  </div>
                )}
              </div>

              {/* content */}
              <div style={{ padding: 8, color: "#fff" }}>
                <strong style={{ fontSize: 14 }}>
                  {c.title || c.content || "Untitled"}
                </strong>

                <div style={{ fontSize: 12, color: "#aaa", marginTop: 4 }}>
                  @{author?.username || "Anon"}
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    marginTop: 6,
                    fontSize: 12,
                    color: "#9aa",
                  }}
                >
                  <span>👁 {analytics.views || 0}</span>
<span>🔥 Impact {analytics.aye || 0}</span>
<span style={{ color: (analytics.aye >= analytics.no) ? "#2ecc71" : "#ff4d4f" }}>
  {(analytics.aye >= analytics.no) ? "↑" : "↓"}
  {Math.abs(
    (analytics.views / (analytics.aye || 1)) -
    (analytics.views / (analytics.no || 1))
  ).toFixed(1)}
</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* fullscreen modal */}
      {openFor && (
        <ChallengeDetailsModal
          open={!!openFor}
          challengeId={openFor}
          onClose={() => setOpenFor(null)}
        />
      )}
    </div>
  );
}
